import React, { useEffect, useState } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withTranslation } from 'react-i18next'
import { updateMedia as actionUM } from '../../../../actions/content/media'
import {
    createMediaSchedule as actionCMS,
    deleteMediaSchedule as actionDMS,
    updateMediaSchedule as actionUMS,
} from '../../../../actions/content/media/mediaSchedule'
import { Confirmation, Input, Link, Form, UnstyledLink, Padding } from '../../../../components'
import validation, { validateWeekdays } from '../validation'
import { ObjectUtils, ArrayUtils } from '../../../../utils'
import WeekSchedule from './WeekSchedule'



const { Label } = Form

const MediaUpdateModal = ({ children, createMediaSchedule, deleteMediaSchedule, inSelectedPlaylist, media, t, updateMedia, updateMediaSchedule }) => {
    const { date_end: currentDateEnd, date_start: currentDateStart, id, name, schedules } = media

    const [dateEnd, setDateEnd] = useState(currentDateEnd)
    const [dateStart, setDateStart] = useState(currentDateStart)
    const [weekDaysSchedule, setWeekDaysSchedule] = useState(schedules || [])
    const [formErrors, setFormErrors] = useState({})

    const { date_end: errorDateEnd, date_start: errorDateStart } = formErrors

    useEffect(() => {
        setDateEnd(currentDateEnd)
    }, [currentDateEnd])

    useEffect(() => {
        setDateStart(currentDateStart)
    }, [currentDateStart])

    useEffect(() => {
        setWeekDaysSchedule(schedules || [])
    }, [schedules])

   

    // Build form value
    const buildForm = () => ({
        date_end: dateEnd,
        date_start: dateStart,
        id,
        name,
    })

    /**
     * On Submit whole form
     */
    const onSubmit = async () => {
        const form = buildForm()
        let errors = validation(form)
        setFormErrors(errors)

        if (!ObjectUtils.isObjValuesEmpty(errors)) {
            return false
        }

        await updateMedia(form)

        errors = validateWeekdays(weekDaysSchedule)
        setFormErrors(errors)

        if (errors.some((item) => !ObjectUtils.isObjValuesEmpty(item))) {
            return false
        }

        const upsertSchedules = []

        await Promise.all(
            weekDaysSchedule.map((mediaSchedule) => {
                const { id: msID } = mediaSchedule
                upsertSchedules.push(msID)

                if (msID) {
                    return updateMediaSchedule(mediaSchedule, inSelectedPlaylist)
                }

                return createMediaSchedule({ ...mediaSchedule, media_id: id }, inSelectedPlaylist)
            })
        )

        const toDelete = (schedules || []).filter(({ id: scheduleID }) => !upsertSchedules.includes(scheduleID))

        await Promise.all(toDelete.map(({ id: toDeleteId }) => deleteMediaSchedule(toDeleteId, id, inSelectedPlaylist)))
    }

    /**
     * Build callback for weekday change
     * @param {Number} index
     */
    const builOnChangeWeekDay = (index) => (values) => {
        const oldEntry = weekDaysSchedule[index]
        const { timeEnd, timeStart, weekDayEnd, weekDayStart } = values
        const newEntry = {
            ...oldEntry,
            end_week_day: weekDayEnd,
            start_week_day: weekDayStart,
            end_time: timeEnd,
            start_time: timeStart,
        }

        const newArray = ArrayUtils.insertItem(weekDaysSchedule, index, newEntry)
        setWeekDaysSchedule(newArray)
    }

    /**
     * Add a new weekly scheduling
     * @param {Object} e event
     */
    const addNewWeekDaySchedule = (e) => {
        e.preventDefault()
        e.stopPropagation()
        builOnChangeWeekDay(weekDaysSchedule.length)({
            weekDayEnd: null,
            weekDayStart: null,
        })
    }

    /**
     * Remove a weekly schedule item
     * @param {Number} index Index to be removed
     */
    const removeNewWeekDaySchedule = (index) => {
        setWeekDaysSchedule(ArrayUtils.removeItem(weekDaysSchedule, index))
    }

    /**
     * Render Start Date label
     */
    const renderStartDateLabel = () => (
        <>
            {t('date-start')}
            &nbsp;
            <Link onClick={() => setDateStart(null)}>&times;</Link>
        </>
    )

    /**
     * Render End label
     */
    const renderEndDateLabel = () => (
        <>
            {t('date-end')}
            &nbsp;
            <Link onClick={() => setDateEnd(null)}>&times;</Link>
        </>
    )

    const renderWeekDayScheduleLabel = () => (
        <UnstyledLink onClick={addNewWeekDaySchedule} to="">
            {t('schedule-weekly')}
            &nbsp;+
        </UnstyledLink>
    )

    

    const renderBody = () => (
        <>
            {!!dateStart && (
                <Label id="date_start" label={renderStartDateLabel()} error={errorDateStart}>
                    <Input
                        id="date_start"
                        name="date_start"
                        type="date"
                        error={!!errorDateStart}
                        onChange={(date) => setDateStart(new Date(date).getTime())}
                        value={dateStart}
                        withTime
                    />
                </Label>
            )}
            {!dateStart && (
                <div>
                    <Link onClick={() => setDateStart(currentDateEnd || new Date().getTime())}>{t('date-start-add')}</Link>
                </div>
            )}
            {!!dateEnd && (
                <Label label={renderEndDateLabel()} error={errorDateEnd}>
                    <Input
                        id="date_end"
                        name="date_end"
                        type="date"
                        error={!!errorDateEnd}
                        onChange={(date) => setDateEnd(new Date(date).getTime())}
                        value={dateEnd}
                        withTime
                    />
                </Label>
            )}
            {!dateEnd && (
                <div>
                    <Link onClick={() => setDateEnd(currentDateEnd || new Date().getTime())}>{t('date-end-add')}</Link>
                </div>
            )}
            <Padding top>
                <Label id="week_days" label={renderWeekDayScheduleLabel()} error={errorDateEnd}>
                    {weekDaysSchedule.map((wkDayS, index) => (
                        <WeekSchedule
                            id="week_days"
                            key={index} // eslint-disable-line react/no-array-index-key
                            timeEnd={wkDayS.end_time}
                            timeStart={wkDayS.start_time}
                            weekDayEnd={wkDayS.end_week_day}
                            weekDayStart={wkDayS.start_week_day}
                            onChange={builOnChangeWeekDay(index)}
                            onRemove={() => removeNewWeekDaySchedule(index)}
                        />
                    ))}
                </Label>

            </Padding>
        </>
    )

    return (
        <Confirmation header={t('scheduling')} confirmation={renderBody()} ok={t('save')} onConfirm={onSubmit}>
            {children}
        </Confirmation>
    )
}

MediaUpdateModal.propTypes = {
    children: PropTypes.node,
    createMediaSchedule: PropTypes.func.isRequired,
    deleteMediaSchedule: PropTypes.func.isRequired,
    inSelectedPlaylist: PropTypes.bool,
    media: PropTypes.shape({
        date_end: PropTypes.number,
        date_start: PropTypes.number,
        id: PropTypes.number,
        schedules: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    t: PropTypes.func.isRequired,
    updateMedia: PropTypes.func.isRequired,
    updateMediaSchedule: PropTypes.func.isRequired,
}

MediaUpdateModal.defaultProps = {
    children: null,
    inSelectedPlaylist: false,
    media: {},
}

const mapDispatchToProps = (dispatch) =>
    bindActionCreators(
        {
            createMediaSchedule: actionCMS,
            deleteMediaSchedule: actionDMS,
            updateMedia: actionUM,
            updateMediaSchedule: actionUMS,
        },
        dispatch
    )

export default connect(null, mapDispatchToProps)(withTranslation()(MediaUpdateModal))
